<template>
  <v-card id="polygon-import-dialog">
    <v-card-title class="primary white--text">
      <v-row>
        <v-col>
          <span>
            {{ title }}
          </span>
        </v-col>
        <v-col cols="auto">
          <BaseDialogActions
          hideRefresh
          hideFullscreen/>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-container fluid>
        <v-row justify="center" v-if="loading">
          <v-col cols="auto">
            <v-progress-circular color="primary" indeterminate></v-progress-circular>
          </v-col>
        </v-row>
        <v-row v-else align="center">
          <v-col>
            <v-file-input
            id="file-input"
            @change="fileChanged"
            :label="$t('chooseFile')">
            </v-file-input>
          </v-col>
          <v-col cols="auto">
            <v-tooltip color="info" bottom min-width="340">
              <template #activator="{ on }">
                <v-icon v-on="on" color="info" large>mdi-help-circle-outline</v-icon>
              </template>
              <v-row dense>
                <v-col cols="12">
                  <span class="white--text subtitle-1">{{ $t('shapefileOrGeojson') }}</span>
                </v-col>
              </v-row>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions class="mx-0">
      <v-row justify="end" class="ma-0">
        <v-col cols="auto">
          <v-btn
          :disabled="importButtonDisabled"
          id="confirm-import"
          @click="importClicked"
          color="primary">
            {{ $t('import') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>
<script>
import { getGeojsonFromShapefile, SHAPEFILE_TYPES } from '@/utils/map/Shapefiles'
import { AngelGeojson, GEOJSON_FILE_TYPE, readGeojsonFile, AngelGeojsonLayer } from '@/utils/map/Geojson'
import { fileNameSymbolsValid } from '@/utils/Files.js'

const CUT = AngelGeojsonLayer.Cut

class GeojsonImportModule {
  angelGeojson
  constructor (angelGeojson) {
    this.angelGeojson = angelGeojson
  }
}

export default {
  name: 'PolygonImportDialog',

  props: {
    tract: {
      type: Object,
      required: true
    }
  },

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  data: () => ({
    loading: false,
    file: null
  }),

  computed: {
    title () {
      return this.$t('importShape')
    },

    fileIsJson () {
      return this.file?.type === GEOJSON_FILE_TYPE
    },

    fileIsShapefile () {
      return SHAPEFILE_TYPES.includes(this.file?.type)
    },

    importButtonDisabled () {
      return this.loading || !this.file
    }
  },

  methods: {
    fileChanged (file) {
      this.file = file
    },

    importClicked () {
      if (!this.validateSize()) return

      const shapefileNameValidResult = fileNameSymbolsValid(this.file?.name)
      if (shapefileNameValidResult !== true) {
        this.setSnackError(shapefileNameValidResult)
        return
      }

      if (this.fileIsJson) {
        this.onGeojsonImported()
      } else if (this.fileIsShapefile) {
        this.onShapefileImported()
      } else {
        this.setSnackError(this.$t('unsupportedFileType'))
      }
    },

    async onGeojsonImported () {
      this.loading = true
      try {
        const geojson = await readGeojsonFile(this.file)
        const angelGeojson = new AngelGeojson(geojson)
        if (!angelGeojson.layerIsNotEmpty(CUT)) {
          this.setSnackError(this.$t('importedShapeContainsNoPolygons'))
          return
        }
        this.emitGeojson(angelGeojson)
      } catch (e) {
        this.setSnackError(this.$t('uploadedShapefileCannotDecode'))
      } finally {
        this.loading = false
      }
    },

    async onShapefileImported () {
      this.loading = true
      try {
        const geojsonFromShapefile = await getGeojsonFromShapefile(this.file)
        if (!geojsonFromShapefile) {
          this.setSnackError(this.$t('uploadedShapefileCannotDecode'))
          return
        }
        const angelGeojson = new AngelGeojson(geojsonFromShapefile)
        if (!angelGeojson.layerIsNotEmpty(CUT)) {
          this.setSnackError(this.$t('importedShapeContainsNoPolygons'))
          return
        }
        this.emitGeojson(angelGeojson)
      } catch (e) {
        this.setSnackError(this.$t('uploadedShapefileCannotDecode'))
      } finally {
        this.loading = false
      }
    },

    validateSize () {
      const MAX_KB = 100
      const maxSize = MAX_KB * 1024
      if (this.file?.size > maxSize) {
        this.setSnackError(this.$t('fileIsOverNKb', { n: MAX_KB }))
        return false
      }
      return true
    },

    emitGeojson (angelGeojson) {
      this.$emit('geojson-import-initiated', new GeojsonImportModule(angelGeojson))
    }
  }
}
</script>
